import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Link, useNavigate } from "react-router-dom";

import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { Cart, Person, BoxArrowRight, List } from "react-bootstrap-icons";
import { useCart } from "react-use-cart";
import { useSelector } from "react-redux";

import AuthService from "../services/auth.service";
import EventBus from "../common/EventBus";
import logo from "../assets/logoAux.png";

const NavMainLinks = memo(({ showModeratorBoard, showAdminBoard, currentUser, closeMenu }) => {
  return (
    <Nav className="me-auto nav-main-links">
      {showModeratorBoard && (
        <Nav.Link as={Link} onClick={closeMenu} to="/empleado" className="nav-link-custom">
          Empleado
        </Nav.Link>
      )}
      {showAdminBoard && (
        <Nav.Link as={Link} onClick={closeMenu} to="/admin" className="nav-link-custom">
          Administrador
        </Nav.Link>
      )}
      {currentUser && (
        <Nav.Link as={Link} onClick={closeMenu} to="/tienda" className="nav-link-custom">
          Tienda
        </Nav.Link>
      )}
      {currentUser && (
        <Nav.Link as={Link} onClick={closeMenu} to="/pedidos" className="nav-link-custom">
          Mis Pedidos
        </Nav.Link>
      )}
      {currentUser && (
        <Nav.Link as={Link} onClick={closeMenu} to="/etiquetas" className="nav-link-custom">
          Etiquetas
        </Nav.Link>
      )}
    </Nav>
  );
});

const CartDropdown = memo(({ closeMenu }) => {
  const { totalItems, cartTotal, emptyCart } = useCart();
  const [showCard, setShowCard] = useState(false);

  const limpiarCarrito = useCallback(() => setShowCard(true), []);
  const cerrarModalCard = useCallback(() => setShowCard(false), []);
  const cerrarModalCardVaciandoCarrito = useCallback(() => {
    emptyCart();
    setShowCard(false);
  }, [emptyCart]);

  return (
    <>
      <NavDropdown
        title={
          <span className="cart-toggle">
            <span className="cart-icon-wrap">
              <Cart size={20} />
              <Badge pill className="cart-count">
                {totalItems.toFixed(0)}
              </Badge>
            </span>
            <span className="cart-total">${cartTotal.toFixed(2)}</span>
          </span>
        }
        id="cart-dropdown"
        className="cart-dropdown"
        align="end"
      >
        <NavDropdown.Item as={Link} to="/cart" onClick={closeMenu}>
          Ver Carrito
        </NavDropdown.Item>
        <NavDropdown.Divider />
        <NavDropdown.Item
          onClick={() => {
            limpiarCarrito();
            closeMenu();
          }}
        >
          Limpiar Carrito
        </NavDropdown.Item>
      </NavDropdown>

      <Modal show={showCard} fullscreen={true} onHide={cerrarModalCard}>
        <Modal.Header>
          <Modal.Title>Alerta</Modal.Title>
          <Button variant="outline-danger" onClick={cerrarModalCard}>
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Row className="d-flex justify-content-center">
            <Col>Desea Vaciar el carrito?</Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={cerrarModalCardVaciandoCarrito}>
            Vaciar
          </Button>
          <Button variant="success" onClick={cerrarModalCard}>
            Salir
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

const UserDropdown = memo(({ currentUser, logOut, closeMenu }) => {
  return (
    <NavDropdown
      title={
        <span className="user-toggle">
          <Person size={20} />
          <span className="user-name">{currentUser.username}</span>
        </span>
      }
      id="user-dropdown"
      className="user-dropdown"
      align="end"
    >
      <NavDropdown.Item as={Link} to="/profile" onClick={closeMenu}>
        Mi perfil
      </NavDropdown.Item>
      <NavDropdown.Divider />
      <NavDropdown.Item onClick={logOut}>
        <BoxArrowRight size={16} className="me-2" />
        Salir
      </NavDropdown.Item>
    </NavDropdown>
  );
});

const UserActions = memo(({ currentUser, closeMenu, logOut }) => {
  const userState = useSelector((state) => state.user.value);

  return (
    <Nav className="nav-user-actions ms-auto">
      {currentUser ? (
        <>
          {currentUser.cuenta_corriente != null && (
            <Badge
              pill
              className={`saldo-badge ${
                parseFloat(userState.cuenta_corriente.saldo) >= 0
                  ? "saldo-positivo"
                  : "saldo-negativo"
              }`}
            >
              {parseFloat(userState.cuenta_corriente.saldo).toFixed(2)}
            </Badge>
          )}

          <Nav.Link as={Link} onClick={closeMenu} to="/mis-precios" className="nav-link-custom">
            Mis precios
          </Nav.Link>

          <CartDropdown closeMenu={closeMenu} />
          <UserDropdown currentUser={currentUser} logOut={logOut} closeMenu={closeMenu} />
        </>
      ) : (
        <>
          <Nav.Link as={Link} onClick={closeMenu} to="/login" className="nav-link-custom">
            Ingresar
          </Nav.Link>
          <Nav.Link as={Link} onClick={closeMenu} to="/register">
            <Button variant="dark" size="sm" className="btn-register">
              Registrarse
            </Button>
          </Nav.Link>
        </>
      )}
    </Nav>
  );
});

const AppNavbar = () => {
  const [showModeratorBoard, setShowModeratorBoard] = useState(false);
  const [showAdminBoard, setShowAdminBoard] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [expanded, setExpanded] = useState(false);

  const navigate = useNavigate();
  const logoutHandlerRef = useRef(null);

  const logOut = useCallback(() => {
    AuthService.logout();
    navigate("/");
    setShowModeratorBoard(false);
    setShowAdminBoard(false);
    setCurrentUser(undefined);
    setExpanded(false);
  }, [navigate]);

  const closeMenu = useCallback(() => setExpanded(false), []);
  const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      setShowModeratorBoard(user.roles.includes("ROLE_MODERATOR"));
      setShowAdminBoard(user.roles.includes("ROLE_ADMIN"));
    }

    logoutHandlerRef.current = EventBus.on("logout", logOut);

    return () => {
      if (logoutHandlerRef.current) {
        EventBus.remove("logout", logoutHandlerRef.current);
      }
    };
  }, [logOut]);

  return (
    <Navbar expand="lg" className="custom-navbar sticky-top">
      <Container>
        <Navbar.Brand as={Link} to="/" className="navbar-brand-custom">
          <img
            src={logo}
            width="32"
            height="32"
            className="brand-logo"
            alt="José Karam y Otros S.H"
          />
          <span className="brand-text">Etereo</span>
        </Navbar.Brand>
        <button
          type="button"
          aria-controls="main-navbar"
          aria-expanded={expanded}
          aria-label="Toggle navigation"
          className="navbar-toggler navbar-toggle-custom"
          onClick={handleToggle}
        >
          <List size={24} />
        </button>
        <div
          id="main-navbar"
          className={`navbar-collapse-custom ${expanded ? "show" : ""}`}
        >
          <NavMainLinks
            showModeratorBoard={showModeratorBoard}
            showAdminBoard={showAdminBoard}
            currentUser={currentUser}
            closeMenu={closeMenu}
          />
          <UserActions
            currentUser={currentUser}
            closeMenu={closeMenu}
            logOut={logOut}
          />
        </div>
      </Container>
    </Navbar>
  );
};

export default AppNavbar;
